import React, { useState, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Crown, TrendingUp, ShoppingBag, DollarSign, Download, ChevronDown, ChevronUp, X, AlertTriangle, Zap, Search, Star, Shield, Headphones, BarChart3, Megaphone, CreditCard, ArrowRight, XCircle, Wallet, Bell, Package, History, FileText, Info, ShieldCheck, Check } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { usePackages, BASE_PRICES, ReportPackage } from '../../contexts/PackagesContext';
interface SubscriptionTabProps {
  showToast: (msg: string) => void;
}
const PAYMENTS = [{
  id: 1,
  date: '15 Мар 2026',
  desc: 'Продление PRO',
  amount: '990 ₽',
  method: 'Visa ···4582',
  status: 'paid' as const
}, {
  id: 2,
  date: '15 Фев 2026',
  desc: 'Продление PRO',
  amount: '990 ₽',
  method: 'Visa ···4582',
  status: 'paid' as const
}, {
  id: 3,
  date: '15 Янв 2026',
  desc: 'Продление PRO',
  amount: '990 ₽',
  method: 'Visa ···4582',
  status: 'paid' as const
}, {
  id: 4,
  date: '15 Дек 2025',
  desc: 'Продление PRO',
  amount: '990 ₽',
  method: 'Visa ···4582',
  status: 'paid' as const
}, {
  id: 5,
  date: '15 Ноя 2025',
  desc: 'Переход на PRO',
  amount: '990 ₽',
  method: 'Visa ···4582',
  status: 'paid' as const
}];
const PLANS = [{
  id: 'free',
  name: 'Базовый',
  price: 0,
  priceLabel: '0 ₽',
  period: 'бесплатно',
  color: 'slate',
  features: {
    consultations: '5 / мес',
    commission: '25%',
    priority: 'Нет',
    limits: '3 услуги',
    advertising: 'Нет',
    analytics: 'Базовая',
    support: 'Email',
    packagesDiscount: 'Нет'
  },
  included: ['5 консультаций/мес', 'Комиссия 25%', 'Базовая аналитика', '3 услуги в каталоге'],
  missing: ['Приоритет в поиске', 'Продвижение', 'Расширенная аналитика']
}, {
  id: 'pro',
  name: 'PRO',
  price: 990,
  priceLabel: '990 ₽',
  period: '/мес',
  color: 'indigo',
  popular: true,
  features: {
    consultations: 'Безлимитно',
    commission: '15%',
    priority: 'Да',
    limits: '20 услуг',
    advertising: 'Базовое',
    analytics: 'Полная',
    support: 'Приоритетный',
    packagesDiscount: 'Скидка 10%'
  },
  included: ['Безлимитно консультаций', 'Комиссия 15%', 'Полная аналитика', '20 услуг', 'Приоритет в поиске', 'Базовое продвижение', 'Скидка 10% на пакеты отчетов'],
  missing: ['Топ в каталоге', 'Персональный менеджер']
}, {
  id: 'top',
  name: 'TOP',
  price: 2490,
  priceLabel: '2 490 ₽',
  period: '/мес',
  color: 'amber',
  features: {
    consultations: 'Безлимитно',
    commission: '10%',
    priority: 'Топ',
    limits: 'Безлимитно',
    advertising: 'Премиум',
    analytics: 'Полная + AI',
    support: 'Персональный менеджер',
    packagesDiscount: 'Скидка 20%'
  },
  included: ['Безлимитно всё', 'Комиссия 10%', 'AI-аналитика', 'Топ в каталоге', 'Премиум продвижение', 'Персональный менеджер', 'Скидка 20% на пакеты отчетов'],
  missing: []
}];
const FEATURE_ROWS = [{
  key: 'consultations',
  label: 'Консультации',
  icon: ShoppingBag
}, {
  key: 'commission',
  label: 'Комиссия',
  icon: DollarSign
}, {
  key: 'priority',
  label: 'Приоритет в поиске',
  icon: Search
}, {
  key: 'limits',
  label: 'Лимит услуг',
  icon: BarChart3
}, {
  key: 'advertising',
  label: 'Продвижение',
  icon: Megaphone
}, {
  key: 'analytics',
  label: 'Аналитика',
  icon: TrendingUp
}, {
  key: 'support',
  label: 'Поддержка',
  icon: Headphones
}, {
  key: 'packagesDiscount',
  label: 'Скидка на пакеты',
  icon: CreditCard
}];
export function SubscriptionTab({
  showToast
}: SubscriptionTabProps) {
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [expandedPayment, setExpandedPayment] = useState<number | null>(null);
  const [payFromBalance, setPayFromBalance] = useState(true);
  // Packages State
  const {
    packages,
    activePackages,
    totalRemaining,
    totalUsed,
    addPackage,
    getPackagePrice
  } = usePackages();
  const [historyModalPkg, setHistoryModalPkg] = useState<ReportPackage | null>(null);
  const [confirmPurchase, setConfirmPurchase] = useState<20 | 50 | 100 | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState<20 | 50 | 100 | null>(null);
  const [upgradePlan, setUpgradePlan] = useState<(typeof PLANS)[number] | null>(null);
  const [upgradePayMethod, setUpgradePayMethod] = useState<'balance' | 'card'>('balance');
  const [upgradeProcessing, setUpgradeProcessing] = useState(false);
  const currentPlanId = 'pro';
  const currentPlan = PLANS.find(p => p.id === currentPlanId)!;
  const accountBalance = 24500;
  const discount = currentPlanId === 'top' ? 20 : currentPlanId === 'pro' ? 10 : 0;
  const discountReason = `тарифу ${currentPlan.name}`;
  const togglePayment = (id: number) => {
    setExpandedPayment(prev => prev === id ? null : id);
  };
  const scrollToPackages = () => {
    const el = document.getElementById('buy-packages-section');
    el?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  const formatPrice = (price: number) => {
    return price.toLocaleString('ru-RU') + ' ₽';
  };
  const handleBuy = (type: 20 | 50 | 100) => {
    setConfirmPurchase(type);
    setPurchaseSuccess(null);
  };
  const handleConfirmPurchase = () => {
    if (!confirmPurchase) return;
    setIsProcessing(true);
    setTimeout(() => {
      addPackage(confirmPurchase, 'astrologer', discount, discountReason);
      setIsProcessing(false);
      setPurchaseSuccess(confirmPurchase);
      setConfirmPurchase(null);
      showToast(`Пакет на ${confirmPurchase} отчётов успешно приобретён!`);
    }, 1500);
  };
  return <div className="space-y-4 sm:space-y-6 animate-in fade-in duration-300" data-id="element-16189">
      {/* Header */}
      <div data-id="element-16190">
        <h2 className="text-lg sm:text-2xl font-bold text-slate-900" data-id="element-16191">
          Подписка и Пакеты
        </h2>
        <p className="text-slate-500 text-xs sm:text-sm mt-0.5" data-id="element-16192">
          Тариф, пакеты отчетов, платежи и выгода
        </p>
      </div>

      {/* Current Plan */}
      <Card padding="sm" className="border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-purple-50/30 sm:p-5" data-id="element-16193">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-3 sm:mb-5" data-id="element-16194">
          <div data-id="element-16195">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5" data-id="element-16196">
              <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" data-id="element-16197" />
              <h3 className="text-base sm:text-xl font-bold text-indigo-900" data-id="element-16198">
                Тариф {currentPlan.name}
              </h3>
              <Badge variant="pro" size="sm" data-id="element-16199">
                Активен
              </Badge>
            </div>
            <p className="text-xs sm:text-sm text-indigo-700" data-id="element-16200">
              Списание: 15 Апреля 2026 ·{' '}
              <span className="font-bold" data-id="element-16201">990 ₽/мес</span>
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto" data-id="element-16202">
            {cancelConfirm ? <div className="flex flex-col gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl w-full sm:w-auto" data-id="element-16203">
                <div className="flex items-center gap-2 text-sm text-rose-700 font-medium" data-id="element-16204">
                  <AlertTriangle className="w-4 h-4" data-id="element-16205" /> Вы потеряете:
                </div>
                <ul className="text-xs text-rose-600 space-y-0.5 ml-6" data-id="element-16206">
                  <li data-id="element-16207">• Приоритет в поиске</li>
                  <li data-id="element-16208">• Скидку {discount}% на пакеты</li>
                  <li data-id="element-16209">• Комиссия вырастет до 25%</li>
                </ul>
                <div className="flex gap-2 mt-1" data-id="element-16210">
                  <Button variant="outline" size="sm" className="border-rose-200 text-rose-600 hover:bg-rose-100" onClick={() => {
                setCancelConfirm(false);
                showToast('Подписка будет отменена в конце периода');
              }} data-id="element-16211">
                    Да, отменить
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setCancelConfirm(false)} data-id="element-16212">
                    Оставить
                  </Button>
                </div>
              </div> : <>
                <Button variant="outline" size="sm" className="text-slate-500 flex-1 sm:flex-none" onClick={() => setCancelConfirm(true)} data-id="element-16213">
                  Отменить
                </Button>
                <Button variant="primary" size="sm" className="flex-1 sm:flex-none" icon={<Zap className="w-4 h-4" data-id="element-16215" />} onClick={() => {
              const el = document.getElementById('plans-section');
              el?.scrollIntoView({
                behavior: 'smooth'
              });
            }} data-id="element-16214">
                  Улучшить тариф
                </Button>
              </>}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-3 sm:mb-5" data-id="element-16216">
          <div className="bg-white/70 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-indigo-100" data-id="element-16217">
            <div className="text-[8px] sm:text-[10px] text-indigo-500 font-medium uppercase tracking-wider mb-0.5" data-id="element-16218">
              Заказы/мес
            </div>
            <div className="text-sm sm:text-lg font-bold text-indigo-900 flex items-center gap-0.5" data-id="element-16219">
              +12{' '}
              <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500" data-id="element-16220" />
            </div>
          </div>
          <div className="bg-white/70 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-indigo-100" data-id="element-16221">
            <div className="text-[8px] sm:text-[10px] text-indigo-500 font-medium uppercase tracking-wider mb-0.5" data-id="element-16222">
              Доход/мес
            </div>
            <div className="text-sm sm:text-lg font-bold text-indigo-900" data-id="element-16223">
              68 400 ₽
            </div>
          </div>
          <div className="bg-white/70 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-indigo-100" data-id="element-16224">
            <div className="text-[8px] sm:text-[10px] text-indigo-500 font-medium uppercase tracking-wider mb-0.5" data-id="element-16225">
              Комиссия
            </div>
            <div className="text-sm sm:text-lg font-bold text-indigo-900" data-id="element-16226">
              15%
            </div>
          </div>
          <div className="bg-white/70 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-indigo-100" data-id="element-16227">
            <div className="text-[8px] sm:text-[10px] text-indigo-500 font-medium uppercase tracking-wider mb-0.5" data-id="element-16228">
              Приоритет
            </div>
            <div className="text-sm sm:text-lg font-bold text-emerald-600 flex items-center gap-0.5" data-id="element-16229">
              <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-16230" /> Да
            </div>
          </div>
        </div>

        {/* What's included */}
        <div className="pt-3 sm:pt-4 border-t border-indigo-100" data-id="element-16231">
          <div className="text-[10px] sm:text-xs font-bold text-indigo-700 uppercase tracking-wider mb-1.5 sm:mb-2" data-id="element-16232">
            Что входит в {currentPlan.name}
          </div>
          <div className="grid grid-cols-2 gap-1" data-id="element-16233">
            {currentPlan.included.map(f => {
            const isPackageDiscount = f.includes('Скидка') && f.includes('пакеты');
            return <div key={f} className={`flex items-center gap-1 text-xs sm:text-sm ${isPackageDiscount ? 'text-indigo-900 font-bold cursor-pointer hover:text-indigo-600 transition-colors' : 'text-indigo-800'}`} onClick={isPackageDiscount ? scrollToPackages : undefined} data-id="element-16234">
                  <CheckCircle2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-500 shrink-0" data-id="element-16235" />{' '}
                  {f}
                  {isPackageDiscount && <ArrowRight className="w-3 h-3 ml-0.5" data-id="element-16236" />}
                </div>;
          })}
          </div>
        </div>
      </Card>

      {/* Payment Method & Notifications */}
      <Card padding="sm" className="sm:p-5" data-id="element-16237">
        <h3 className="font-bold text-slate-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base" data-id="element-16238">
          <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" data-id="element-16239" /> Способ
          оплаты подписки
        </h3>
        <div className="flex gap-2 sm:gap-3 mb-3 sm:mb-4" data-id="element-16240">
          <button onClick={() => {
          setPayFromBalance(true);
          showToast('Подписка будет списываться с баланса');
        }} className={`flex-1 p-3 rounded-xl border text-left transition-colors ${payFromBalance ? 'border-indigo-500 bg-indigo-50/50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-16241">
            <div className="flex items-center gap-2 mb-1" data-id="element-16242">
              <Wallet className="w-4 h-4 text-indigo-600" data-id="element-16243" />
              <span className="font-bold text-sm text-slate-900" data-id="element-16244">
                С баланса
              </span>
              {payFromBalance && <Badge variant="success" size="sm" data-id="element-16245">
                  Активно
                </Badge>}
            </div>
            <div className="text-xs text-slate-500" data-id="element-16246">
              Баланс:{' '}
              <span className="font-bold text-slate-700" data-id="element-16247">
                {new Intl.NumberFormat('ru-RU').format(accountBalance)} ₽
              </span>
            </div>
          </button>
          <button onClick={() => {
          setPayFromBalance(false);
          showToast('Подписка будет списываться с карты');
        }} className={`flex-1 p-3 rounded-xl border text-left transition-colors ${!payFromBalance ? 'border-indigo-500 bg-indigo-50/50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-16248">
            <div className="flex items-center gap-2 mb-1" data-id="element-16249">
              <CreditCard className="w-4 h-4 text-slate-600" data-id="element-16250" />
              <span className="font-bold text-sm text-slate-900" data-id="element-16251">С карты</span>
              {!payFromBalance && <Badge variant="success" size="sm" data-id="element-16252">
                  Активно
                </Badge>}
            </div>
            <div className="text-xs text-slate-500" data-id="element-16253">Visa •••• 4582</div>
          </button>
        </div>
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg" data-id="element-16254">
          <Bell className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" data-id="element-16255" />
          <div className="text-xs text-amber-700" data-id="element-16256">
            <span className="font-medium" data-id="element-16257">Уведомление:</span> за 3 дня до
            списания вы получите напоминание. Следующее списание{' '}
            <span className="font-bold" data-id="element-16258">990 ₽</span> — 15 Апреля 2026{' '}
            {payFromBalance ? 'с баланса' : 'с карты'}.
          </div>
        </div>
      </Card>

      {/* Why Upgrade */}
      <Card padding="sm" className="border-amber-200 bg-amber-50/30 sm:p-5" data-id="element-16259">
        <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3" data-id="element-16260">
          <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" data-id="element-16261" />
          <h3 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-16262">
            Почему стоит улучшить тариф
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3" data-id="element-16263">
          {[{
          stat: '+40%',
          label: 'заказов',
          desc: 'На TOP на 40% больше заказов'
        }, {
          stat: 'Топ-3',
          label: 'в каталоге',
          desc: 'Профиль первым в поиске'
        }, {
          stat: '-10%',
          label: 'комиссия',
          desc: 'Экономия на комиссии'
        }].map(item => <div key={item.label} className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 border border-amber-100" data-id="element-16264">
              <div className="text-lg sm:text-2xl font-bold text-amber-700 mb-0.5" data-id="element-16265">
                {item.stat}
              </div>
              <div className="text-xs sm:text-sm font-medium text-slate-900 mb-0.5" data-id="element-16266">
                {item.label}
              </div>
              <div className="text-[10px] sm:text-xs text-slate-500 hidden sm:block" data-id="element-16267">
                {item.desc}
              </div>
            </div>)}
        </div>
        <Button variant="primary" size="sm" className="mt-3 sm:mt-4 bg-amber-600 hover:bg-amber-700" icon={<ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-16269" />} onClick={() => {
        const el = document.getElementById('plans-section');
        el?.scrollIntoView({
          behavior: 'smooth'
        });
      }} data-id="element-16268">
          Перейти на TOP
        </Button>
      </Card>

      {/* Plan Comparison */}
      <div id="plans-section" data-id="element-16270">
        <h3 className="font-bold text-slate-900 text-sm sm:text-lg mb-3 sm:mb-4" data-id="element-16271">
          Выбор тарифа
        </h3>

        {/* Mobile: Cards */}
        <div className="sm:hidden space-y-2" data-id="element-16272">
          {PLANS.map(plan => {
          const isCurrent = plan.id === 'pro';
          return <Card key={plan.id} padding="sm" className={`${isCurrent ? 'border-indigo-500 ring-1 ring-indigo-500' : 'border-slate-200'} ${plan.id === 'top' ? 'border-amber-400' : ''}`} data-id="element-16273">
                <div className="flex items-center justify-between mb-1.5" data-id="element-16274">
                  <div className="flex items-center gap-1.5" data-id="element-16275">
                    <span className="font-bold text-base text-slate-900" data-id="element-16276">
                      {plan.name}
                    </span>
                    {isCurrent && <Badge variant="pro" size="sm" data-id="element-16277">
                        Текущий
                      </Badge>}
                    {plan.id === 'top' && !isCurrent && <Badge variant="warning" size="sm" data-id="element-16278">
                        Популярный
                      </Badge>}
                  </div>
                </div>
                <div className="mb-2" data-id="element-16279">
                  <span className="text-xl font-bold text-slate-900" data-id="element-16280">
                    {plan.priceLabel}
                  </span>
                  <span className="text-xs text-slate-500" data-id="element-16281">{plan.period}</span>
                </div>
                <ul className="space-y-1 mb-3" data-id="element-16282">
                  {plan.included.map(f => <li key={f} className="text-xs text-slate-700 flex items-center gap-1" data-id="element-16283">
                      <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" data-id="element-16284" />{' '}
                      {f}
                    </li>)}
                  {plan.missing.map(f => <li key={f} className="text-xs text-slate-400 flex items-center gap-1" data-id="element-16285">
                      <XCircle className="w-3 h-3 text-slate-300 shrink-0" data-id="element-16286" />{' '}
                      {f}
                    </li>)}
                </ul>
                {isCurrent ? <div className="text-center text-xs text-indigo-600 font-medium py-1.5" data-id="element-16287">
                    Ваш текущий тариф
                  </div> : <Button variant={plan.id === 'top' ? 'primary' : 'outline'} fullWidth size="sm" onClick={() => setUpgradePlan(plan)} data-id="element-16288">
                    {plan.price > 990 ? 'Улучшить' : plan.price === 0 ? 'Перейти' : 'Выбрать'}
                  </Button>}
              </Card>;
        })}
        </div>

        {/* Desktop: Comparison Table */}
        <Card padding="none" className="hidden sm:block overflow-hidden" data-id="element-16289">
          <table className="w-full text-sm" data-id="element-16290">
            <thead data-id="element-16291">
              <tr className="border-b border-slate-100" data-id="element-16292">
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 w-1/4" data-id="element-16293">
                  Возможность
                </th>
                {PLANS.map(plan => <th key={plan.id} className={`px-4 py-3 text-center ${plan.id === 'pro' ? 'bg-indigo-50/50' : plan.id === 'top' ? 'bg-amber-50/50' : ''}`} data-id="element-16294">
                    <div className="font-bold text-slate-900" data-id="element-16295">{plan.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5" data-id="element-16296">
                      {plan.priceLabel}
                      {plan.period}
                    </div>
                    {plan.id === 'pro' && <Badge variant="pro" size="sm" className="mt-1" data-id="element-16297">
                        Текущий
                      </Badge>}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-16298">
              {FEATURE_ROWS.map(row => <tr key={row.key} className="hover:bg-slate-50/50" data-id="element-16299">
                  <td className="px-4 py-2.5 text-slate-700 flex items-center gap-2" data-id="element-16300">
                    <row.icon className="w-4 h-4 text-slate-400" data-id="element-16301" /> {row.label}
                  </td>
                  {PLANS.map(plan => <td key={plan.id} className={`px-4 py-2.5 text-center font-medium ${plan.id === 'pro' ? 'bg-indigo-50/30 text-indigo-700' : plan.id === 'top' ? 'bg-amber-50/30 text-amber-700' : 'text-slate-600'}`} data-id="element-16302">
                      {(plan.features as Record<string, string>)[row.key]}
                    </td>)}
                </tr>)}
            </tbody>
            <tfoot data-id="element-16303">
              <tr className="border-t border-slate-200" data-id="element-16304">
                <td className="px-4 py-3" data-id="element-16305"></td>
                {PLANS.map(plan => <td key={plan.id} className={`px-4 py-3 text-center ${plan.id === 'pro' ? 'bg-indigo-50/30' : plan.id === 'top' ? 'bg-amber-50/30' : ''}`} data-id="element-16306">
                    {plan.id === 'pro' ? <span className="text-sm text-indigo-600 font-medium" data-id="element-16307">
                        Текущий тариф
                      </span> : <Button variant={plan.id === 'top' ? 'primary' : 'outline'} size="sm" fullWidth onClick={() => setUpgradePlan(plan)} data-id="element-16308">
                        {plan.price > 990 ? 'Улучшить' : 'Выбрать'}
                      </Button>}
                  </td>)}
              </tr>
            </tfoot>
          </table>
        </Card>
      </div>

      {/* Payment History */}
      <div data-id="element-16309">
        <h3 className="font-bold text-slate-900 text-lg mb-4" data-id="element-16310">
          История платежей
        </h3>
        <Card padding="none" className="overflow-hidden" data-id="element-16311">
          {/* Desktop */}
          <div className="hidden sm:block" data-id="element-16312">
            <table className="w-full text-sm text-left" data-id="element-16313">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-100" data-id="element-16314">
                <tr data-id="element-16315">
                  <th className="px-4 py-2.5 font-medium text-xs" data-id="element-16316">Дата</th>
                  <th className="px-4 py-2.5 font-medium text-xs" data-id="element-16317">Описание</th>
                  <th className="px-4 py-2.5 font-medium text-xs" data-id="element-16318">Способ</th>
                  <th className="px-4 py-2.5 font-medium text-xs" data-id="element-16319">Сумма</th>
                  <th className="px-4 py-2.5 font-medium text-xs" data-id="element-16320">Статус</th>
                  <th className="px-4 py-2.5 font-medium text-xs" data-id="element-16321"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-16322">
                {PAYMENTS.map(p => <Fragment key={p.id} data-id="element-16323">
                    <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => togglePayment(p.id)} data-id="element-16324">
                      <td className="px-4 py-2.5 text-slate-600 text-xs" data-id="element-16325">
                        {p.date}
                      </td>
                      <td className="px-4 py-2.5 font-medium text-slate-900 text-xs" data-id="element-16326">
                        {p.desc}
                      </td>
                      <td className="px-4 py-2.5 text-slate-500 text-xs" data-id="element-16327">
                        {p.method}
                      </td>
                      <td className="px-4 py-2.5 text-slate-900 text-xs font-medium" data-id="element-16328">
                        {p.amount}
                      </td>
                      <td className="px-4 py-2.5" data-id="element-16329">
                        <Badge variant="success" size="sm" data-id="element-16330">
                          Оплачено
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5" data-id="element-16331">
                        {expandedPayment === p.id ? <ChevronUp className="w-4 h-4 text-slate-400" data-id="element-16332" /> : <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-16333" />}
                      </td>
                    </tr>
                    {expandedPayment === p.id && <tr data-id="element-16334">
                        <td colSpan={6} className="px-4 py-3 bg-slate-50" data-id="element-16335">
                          <div className="flex items-center justify-between" data-id="element-16336">
                            <div className="text-xs text-slate-500 space-y-1" data-id="element-16337">
                              <div data-id="element-16338">
                                Чек №{1000 + p.id} · {p.date}
                              </div>
                              <div data-id="element-16339">Способ оплаты: {p.method}</div>
                              <div data-id="element-16340">Тариф: PRO · Период: 1 месяц</div>
                            </div>
                            <Button variant="outline" size="sm" icon={<Download className="w-3.5 h-3.5" data-id="element-16342" />} onClick={e => {
                        e.stopPropagation();
                        showToast('Чек скачан ✓');
                      }} data-id="element-16341">
                              Скачать чек
                            </Button>
                          </div>
                        </td>
                      </tr>}
                  </Fragment>)}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="sm:hidden divide-y divide-slate-100" data-id="element-16343">
            {PAYMENTS.map(p => <div key={p.id} data-id="element-16344">
                <div className="p-3 flex justify-between items-center cursor-pointer" onClick={() => togglePayment(p.id)} data-id="element-16345">
                  <div data-id="element-16346">
                    <div className="text-sm font-medium text-slate-900" data-id="element-16347">
                      {p.desc}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-16348">
                      {p.date} · {p.method}
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-16349">
                    <div className="text-right" data-id="element-16350">
                      <div className="text-sm font-bold text-slate-900" data-id="element-16351">
                        {p.amount}
                      </div>
                      <Badge variant="success" size="sm" data-id="element-16352">
                        Оплачено
                      </Badge>
                    </div>
                    {expandedPayment === p.id ? <ChevronUp className="w-4 h-4 text-slate-400" data-id="element-16353" /> : <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-16354" />}
                  </div>
                </div>
                {expandedPayment === p.id && <div className="px-3 pb-3" data-id="element-16355">
                    <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-id="element-16356">
                      <div className="text-xs text-slate-500" data-id="element-16357">
                        Чек №{1000 + p.id} · Тариф PRO · 1 месяц
                      </div>
                      <Button variant="outline" size="sm" fullWidth icon={<Download className="w-3.5 h-3.5" data-id="element-16359" />} onClick={() => showToast('Чек скачан ✓')} data-id="element-16358">
                        Скачать чек
                      </Button>
                    </div>
                  </div>}
              </div>)}
          </div>
        </Card>
      </div>

      {/* Packages Summary */}
      <Card padding="sm" className="border-slate-200 sm:p-5" data-id="element-16360">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6" data-id="element-16361">
          <div className="flex items-center gap-2 sm:gap-3" data-id="element-16362">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600" data-id="element-16363">
              <Package className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-16364" />
            </div>
            <div data-id="element-16365">
              <h3 className="text-sm sm:text-lg font-bold text-slate-900" data-id="element-16366">
                Сводка по пакетам
              </h3>
              <p className="text-xs text-slate-500" data-id="element-16367">Управление отчетами</p>
            </div>
          </div>
          {discount > 0 && <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-2 sm:p-3 text-right" data-id="element-16368">
              <div className="text-xs sm:text-sm font-bold text-emerald-700" data-id="element-16369">
                Скидка на пакеты: {discount}%
              </div>
              <div className="text-[10px] sm:text-xs text-emerald-600" data-id="element-16370">
                По тарифу {currentPlan.name}
              </div>
            </div>}
        </div>

        {activePackages.length > 0 ? <div className="space-y-4" data-id="element-16371">
            {activePackages.map(pkg => {
          const progress = pkg.usedReports / pkg.totalReports * 100;
          return <div key={pkg.id} className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-16372">
                  <div className="flex justify-between items-center mb-3" data-id="element-16373">
                    <div className="font-bold text-slate-900 text-lg" data-id="element-16374">
                      {pkg.title}
                    </div>
                    <Badge variant="success" data-id="element-16375">Активен</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-16376">
                    <div data-id="element-16377">
                      <div className="text-xs text-slate-500 mb-1" data-id="element-16378">
                        Всего отчетов
                      </div>
                      <div className="font-bold text-slate-900" data-id="element-16379">
                        {pkg.totalReports}
                      </div>
                    </div>
                    <div data-id="element-16380">
                      <div className="text-xs text-slate-500 mb-1" data-id="element-16381">
                        Использовано
                      </div>
                      <div className="font-bold text-slate-900" data-id="element-16382">
                        {pkg.usedReports}
                      </div>
                    </div>
                    <div data-id="element-16383">
                      <div className="text-xs text-slate-500 mb-1" data-id="element-16384">
                        Осталось
                      </div>
                      <div className="font-bold text-indigo-600 text-lg" data-id="element-16385">
                        {pkg.remainingReports}
                      </div>
                    </div>
                  </div>
                  <div data-id="element-16386">
                    <div className="flex justify-between text-xs font-medium mb-1.5" data-id="element-16387">
                      <span className="text-slate-500" data-id="element-16388">
                        Прогресс использования
                      </span>
                      <span className="text-slate-900" data-id="element-16389">
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden" data-id="element-16390">
                      <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{
                  width: `${progress}%`
                }} data-id="element-16391" />
                    </div>
                  </div>
                </div>;
        })}
          </div> : <div className="py-8 bg-slate-50 rounded-xl border border-slate-100 border-dashed" data-id="element-16392">
            <div className="text-center mb-6" data-id="element-16393">
              <div className="text-4xl mb-3" data-id="element-16394">📦</div>
              <h4 className="text-slate-900 font-bold text-lg mb-2" data-id="element-16395">
                У вас пока нет пакетов отчётов
              </h4>
              <p className="text-sm text-slate-500 max-w-md mx-auto" data-id="element-16396">
                Пакет — это набор предоплаченных отчётов. Вместо оплаты каждого
                отчёта отдельно, купите пакет и экономьте до 20%.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 px-6 mb-6" data-id="element-16397">
              <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-16398">
                <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold" data-id="element-16399">
                  1
                </div>
                Купите пакет
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 hidden sm:block" data-id="element-16400" />
              <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-16401">
                <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold" data-id="element-16402">
                  2
                </div>
                Создавайте отчёты
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 hidden sm:block" data-id="element-16403" />
              <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-16404">
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 text-xs font-bold" data-id="element-16405">
                  ✓
                </div>
                Экономьте
              </div>
            </div>
            {discount > 0 && <div className="text-center mb-4" data-id="element-16406">
                <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200" data-id="element-16407">
                  Ваша скидка {discount}% по тарифу {currentPlan.name} уже
                  активна
                </span>
              </div>}
            <div className="text-center" data-id="element-16408">
              <Button variant="primary" onClick={scrollToPackages} data-id="element-16409">
                Выбрать пакет
              </Button>
            </div>
          </div>}
      </Card>

      {/* Active Packages List */}
      {activePackages.length > 0 && <div data-id="element-16410">
          <h3 className="font-bold text-slate-900 text-lg mb-4" data-id="element-16411">
            Активные пакеты
          </h3>
          <div className="space-y-4" data-id="element-16412">
            {activePackages.map(pkg => {
          const progress = pkg.usedReports / pkg.totalReports * 100;
          const basePrice = BASE_PRICES[pkg.type];
          return <Card key={pkg.id} className="border-slate-200 overflow-hidden" data-id="element-16413">
                  <div className="p-5 flex flex-col md:flex-row justify-between gap-6" data-id="element-16414">
                    <div className="flex-1" data-id="element-16415">
                      <div className="flex items-center gap-3 mb-3" data-id="element-16416">
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 flex-shrink-0" data-id="element-16417">
                          <Package className="w-5 h-5" data-id="element-16418" />
                        </div>
                        <div data-id="element-16419">
                          <h4 className="text-lg font-bold text-slate-900" data-id="element-16420">
                            {pkg.title}
                          </h4>
                          <div className="text-xs text-slate-500" data-id="element-16421">
                            Куплен: {formatDate(pkg.purchaseDate)}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-16422">
                        <div data-id="element-16423">
                          <div className="text-xs text-slate-500 mb-1" data-id="element-16424">
                            Обычная цена
                          </div>
                          <div className="text-sm text-slate-400 line-through" data-id="element-16425">
                            {formatPrice(basePrice)}
                          </div>
                        </div>
                        <div data-id="element-16426">
                          <div className="text-xs text-slate-500 mb-1" data-id="element-16427">
                            Ваша цена
                          </div>
                          <div className="text-sm font-bold text-slate-900" data-id="element-16428">
                            {formatPrice(pkg.purchasePrice)}
                          </div>
                        </div>
                        <div data-id="element-16429">
                          <div className="text-xs text-slate-500 mb-1" data-id="element-16430">
                            Использовано
                          </div>
                          <div className="text-sm font-medium text-slate-900" data-id="element-16431">
                            {pkg.usedReports}
                          </div>
                        </div>
                        <div data-id="element-16432">
                          <div className="text-xs text-slate-500 mb-1" data-id="element-16433">
                            Осталось
                          </div>
                          <div className="text-sm font-bold text-indigo-600" data-id="element-16434">
                            {pkg.remainingReports}
                          </div>
                        </div>
                      </div>

                      <div className="text-xs text-slate-500 mb-4" data-id="element-16435">
                        Цена за 1 отчет:{' '}
                        <span className="font-bold text-slate-900" data-id="element-16436">
                          {formatPrice(pkg.pricePerReport)}
                        </span>
                      </div>

                      <div className="mb-4" data-id="element-16437">
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-16438">
                          <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{
                      width: `${progress}%`
                    }} data-id="element-16439" />
                        </div>
                      </div>

                      <div className="flex gap-3" data-id="element-16440">
                        <Button variant="outline" size="sm" icon={<History className="w-4 h-4" data-id="element-16442" />} onClick={() => setHistoryModalPkg(pkg)} data-id="element-16441">
                          История использования
                        </Button>
                        <Button variant="primary" size="sm" onClick={scrollToPackages} data-id="element-16443">
                          Купить еще
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>;
        })}
          </div>
        </div>}

      {/* Buy Packages */}
      <div id="buy-packages-section" className="pt-2 sm:pt-4" data-id="element-16444">
        {/* Section Header with Explanation */}
        <div className="mb-4 sm:mb-6" data-id="element-16445">
          <h3 className="font-bold text-slate-900 text-sm sm:text-lg mb-1 sm:mb-2" data-id="element-16446">
            Пакеты отчётов
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 max-w-2xl" data-id="element-16447">
            Предоплаченный набор отчётов. Чем больше пакет — тем дешевле каждый
            отчёт.
          </p>
        </div>

        {/* What's in a package - visual explainer */}
        <Card padding="sm" className="mb-4 sm:mb-6 bg-gradient-to-r from-indigo-50/50 to-purple-50/30 border-indigo-100 sm:p-5" data-id="element-16448">
          <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-4" data-id="element-16449">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" data-id="element-16450" />
            <h4 className="font-bold text-slate-900 text-xs sm:text-base" data-id="element-16451">
              Какие отчёты входят в пакет?
            </h4>
          </div>
          <p className="text-xs text-slate-600 mb-2 sm:mb-4" data-id="element-16452">
            1 отчёт = 1 списание. Вы выбираете тип:
          </p>
          <div className="grid grid-cols-5 gap-1.5 sm:gap-3" data-id="element-16453">
            {[{
            icon: '🌟',
            name: 'Натальная',
            desc: 'Разбор личности'
          }, {
            icon: '☀️',
            name: 'Соляр',
            desc: 'Прогноз на год'
          }, {
            icon: '💕',
            name: 'Совмест.',
            desc: 'Синастрия'
          }, {
            icon: '🔮',
            name: 'Транзиты',
            desc: 'Влияния'
          }, {
            icon: '📊',
            name: 'Комбо',
            desc: 'Расширенный'
          }].map(report => <div key={report.name} className="bg-white rounded-lg sm:rounded-xl p-1.5 sm:p-3 border border-indigo-100/50 text-center" data-id="element-16454">
                <div className="text-base sm:text-2xl mb-0.5 sm:mb-1.5" data-id="element-16455">
                  {report.icon}
                </div>
                <div className="text-[9px] sm:text-xs font-bold text-slate-900 leading-tight" data-id="element-16456">
                  {report.name}
                </div>
                <div className="text-[8px] sm:text-[10px] text-slate-500 hidden sm:block" data-id="element-16457">
                  {report.desc}
                </div>
              </div>)}
          </div>
        </Card>

        {/* How it works - 3 steps */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-8" data-id="element-16458">
          <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-4 bg-white rounded-lg sm:rounded-xl border border-slate-100" data-id="element-16459">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-[10px] sm:text-sm flex-shrink-0" data-id="element-16460">
              1
            </div>
            <div data-id="element-16461">
              <div className="text-xs sm:text-sm font-bold text-slate-900" data-id="element-16462">
                Купите
              </div>
              <div className="text-[10px] sm:text-xs text-slate-500 hidden sm:block" data-id="element-16463">
                20, 50 или 100 отчётов
              </div>
            </div>
          </div>
          <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-4 bg-white rounded-lg sm:rounded-xl border border-slate-100" data-id="element-16464">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-[10px] sm:text-sm flex-shrink-0" data-id="element-16465">
              2
            </div>
            <div data-id="element-16466">
              <div className="text-xs sm:text-sm font-bold text-slate-900" data-id="element-16467">
                Создавайте
              </div>
              <div className="text-[10px] sm:text-xs text-slate-500 hidden sm:block" data-id="element-16468">
                Автосписание из пакета
              </div>
            </div>
          </div>
          <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-4 bg-white rounded-lg sm:rounded-xl border border-slate-100" data-id="element-16469">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-[10px] sm:text-sm flex-shrink-0" data-id="element-16470">
              ✓
            </div>
            <div data-id="element-16471">
              <div className="text-xs sm:text-sm font-bold text-slate-900" data-id="element-16472">
                Бессрочно
              </div>
              <div className="text-[10px] sm:text-xs text-slate-500 hidden sm:block" data-id="element-16473">
                Не сгорают со временем
              </div>
            </div>
          </div>
        </div>

        {/* Price comparison hint */}
        {discount > 0 && <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4 p-2 sm:p-3 bg-emerald-50 border border-emerald-100 rounded-lg" data-id="element-16474">
            <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600 flex-shrink-0" data-id="element-16475" />
            <span className="text-xs sm:text-sm text-emerald-800" data-id="element-16476">
              Тариф <span className="font-bold" data-id="element-16477">{currentPlan.name}</span> —
              скидка <span className="font-bold" data-id="element-16478">{discount}%</span> на все
              пакеты.
            </span>
          </div>}

        {/* Package Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6" data-id="element-16479">
          {[20, 50, 100].map(type => {
          const basePrice = BASE_PRICES[type as 20 | 50 | 100];
          const discountedPrice = getPackagePrice(type as 20 | 50 | 100, discount);
          const savings = basePrice - discountedPrice;
          const perReport = Math.round(discountedPrice / type);
          const basePerReport = Math.round(basePrice / type);
          const savingsPercent = Math.round(savings / basePrice * 100);
          const isPopular = type === 50;
          return <Card key={type} className={`flex flex-col overflow-hidden ${isPopular ? 'border-indigo-400 shadow-lg relative transform md:-translate-y-2' : 'border-slate-200'}`} data-id="element-16480">
                {/* Card Header */}
                <div className={`p-3 sm:p-5 pb-2 sm:pb-4 ${isPopular ? 'bg-gradient-to-br from-indigo-50 to-purple-50' : 'bg-slate-50/50'}`} data-id="element-16481">
                  {isPopular && <div className="absolute right-3 sm:right-4 top-0" data-id="element-16482">
                      <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-[9px] sm:text-[10px] font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-b-lg shadow-sm" data-id="element-16483">
                        Популярно
                      </span>
                    </div>}
                  <div className="text-xl sm:text-3xl mb-1 sm:mb-2" data-id="element-16484">
                    {type === 20 ? '📦' : type === 50 ? '🚀' : '💎'}
                  </div>
                  <h4 className="text-base sm:text-xl font-bold text-slate-900 mb-0.5" data-id="element-16485">
                    {type === 20 ? 'Start Pack' : type === 50 ? 'Pro Pack' : 'Max Pack'}
                  </h4>
                  <div className="text-xs sm:text-sm text-slate-600 font-medium" data-id="element-16486">
                    {type} отчётов любого типа
                  </div>
                </div>

                {/* Pricing */}
                <div className="p-3 sm:p-5 flex-grow flex flex-col" data-id="element-16487">
                  <div className="mb-3 sm:mb-5" data-id="element-16488">
                    {discount > 0 ? <>
                        <div className="flex items-baseline gap-1.5 mb-0.5" data-id="element-16489">
                          <span className="text-xs text-slate-400 line-through" data-id="element-16490">
                            {formatPrice(basePrice)}
                          </span>
                          <span className="text-[10px] sm:text-xs font-bold text-emerald-600 bg-emerald-50 px-1 sm:px-1.5 py-0.5 rounded" data-id="element-16491">
                            -{savingsPercent}%
                          </span>
                        </div>
                        <div className="text-2xl sm:text-3xl font-bold text-indigo-600 mb-0.5" data-id="element-16492">
                          {formatPrice(discountedPrice)}
                        </div>
                        <div className="text-[10px] sm:text-xs text-emerald-600 font-medium" data-id="element-16493">
                          Экономия {formatPrice(savings)}
                        </div>
                      </> : <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-0.5" data-id="element-16494">
                        {formatPrice(basePrice)}
                      </div>}
                  </div>

                  {/* Key details */}
                  <div className="space-y-1.5 sm:space-y-3 mb-3 sm:mb-5 flex-grow" data-id="element-16495">
                    <div className="flex items-center justify-between py-1 sm:py-2 border-b border-slate-100" data-id="element-16496">
                      <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-16497">
                        <FileText className="w-3 h-3 text-slate-400" data-id="element-16498" /> За 1
                        отчёт
                      </span>
                      <div className="text-right" data-id="element-16499">
                        {discount > 0 && <span className="text-[10px] text-slate-400 line-through mr-1" data-id="element-16500">
                            {formatPrice(basePerReport)}
                          </span>}
                        <span className="font-bold text-xs sm:text-sm text-slate-900" data-id="element-16501">
                          {formatPrice(perReport)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between py-1 sm:py-2 border-b border-slate-100" data-id="element-16502">
                      <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-16503">
                        <Shield className="w-3 h-3 text-slate-400" data-id="element-16504" /> Срок
                      </span>
                      <span className="font-medium text-slate-900 text-xs" data-id="element-16505">
                        Бессрочно ∞
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1 sm:py-2 border-b border-slate-100" data-id="element-16506">
                      <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-16507">
                        <Star className="w-3 h-3 text-slate-400" data-id="element-16508" /> Отчёты
                      </span>
                      <span className="font-medium text-slate-900 text-xs" data-id="element-16509">
                        Все виды
                      </span>
                    </div>
                  </div>

                  {/* What's included mini-list - hidden on mobile for compactness */}
                  <div className="mb-3 sm:mb-5 p-2 sm:p-3 bg-slate-50 rounded-lg hidden sm:block" data-id="element-16510">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-16511">
                      Что входит
                    </div>
                    <div className="space-y-1" data-id="element-16512">
                      {['Натальные карты', 'Соляры', 'Совместимость', 'Транзиты', 'Комбо-анализ'].map(item => <div key={item} className="flex items-center gap-1 text-xs text-slate-600" data-id="element-16513">
                          <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" data-id="element-16514" />{' '}
                          {item}
                        </div>)}
                    </div>
                  </div>

                  <Button variant={isPopular ? 'primary' : 'outline'} fullWidth size="sm" onClick={() => handleBuy(type as 20 | 50 | 100)} data-id="element-16515">
                    Купить за{' '}
                    {formatPrice(discount > 0 ? discountedPrice : basePrice)}
                  </Button>
                </div>
              </Card>;
        })}
        </div>
      </div>

      {/* Modals */}
      {/* History Modal */}
      {historyModalPkg && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md overflow-y-auto" data-id="element-16516">
            <div className="bg-white rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.3)] max-w-2xl w-full max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200 ring-1 ring-black/5" data-id="element-16517">
              <div className="flex items-center justify-between p-5 border-b border-slate-100" data-id="element-16518">
                <div data-id="element-16519">
                  <h3 className="text-lg font-bold text-slate-900" data-id="element-16520">
                    История использования
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-16521">
                    {historyModalPkg.title}
                  </p>
                </div>
                <button onClick={() => setHistoryModalPkg(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-16522">
                  <X className="w-5 h-5" data-id="element-16523" />
                </button>
              </div>

              <div className="p-5 overflow-y-auto" data-id="element-16524">
                {historyModalPkg.deductions.length === 0 ? <div className="text-center py-8" data-id="element-16525">
                    <p className="text-slate-500" data-id="element-16526">
                      Вы еще не использовали отчеты из этого пакета
                    </p>
                  </div> : <table className="w-full text-sm text-left" data-id="element-16527">
                    <thead className="bg-slate-50 text-slate-500 border-b border-slate-100" data-id="element-16528">
                      <tr data-id="element-16529">
                        <th className="px-4 py-2.5 font-medium text-xs" data-id="element-16530">
                          Дата
                        </th>
                        <th className="px-4 py-2.5 font-medium text-xs" data-id="element-16531">
                          Заказ
                        </th>
                        <th className="px-4 py-2.5 font-medium text-xs" data-id="element-16532">
                          Отчет
                        </th>
                        <th className="px-4 py-2.5 font-medium text-xs text-right" data-id="element-16533">
                          Списание
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-id="element-16534">
                      {historyModalPkg.deductions.map((deduction, idx) => {
                // Calculate remaining at that point in time (simplified for mock)
                const remainingAtTime = historyModalPkg.totalReports - (historyModalPkg.deductions.length - idx);
                return <tr key={deduction.id} className="hover:bg-slate-50" data-id="element-16535">
                            <td className="px-4 py-3 text-slate-600 text-xs" data-id="element-16536">
                              {formatDate(deduction.date)}
                            </td>
                            <td className="px-4 py-3 text-slate-600 text-xs" data-id="element-16537">
                              #{deduction.orderId}
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-900 text-xs" data-id="element-16538">
                              {deduction.reportTitle}
                            </td>
                            <td className="px-4 py-3 text-right" data-id="element-16539">
                              <div className="text-rose-500 font-bold text-xs" data-id="element-16540">
                                -1 отчет
                              </div>
                              <div className="text-[10px] text-slate-500" data-id="element-16541">
                                Остаток: {remainingAtTime}
                              </div>
                            </td>
                          </tr>;
              })}
                    </tbody>
                  </table>}
              </div>
            </div>
          </div>, document.body)}

      {/* Purchase Confirmation Modal */}
      {confirmPurchase && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md overflow-y-auto" data-id="element-16542">
            <div className="bg-white rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.3)] max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200 ring-1 ring-black/5" data-id="element-16543">
              <div className="flex items-center justify-between p-5 border-b border-slate-100" data-id="element-16544">
                <h3 className="text-lg font-bold text-slate-900" data-id="element-16545">
                  Подтверждение покупки
                </h3>
                <button onClick={() => {
            setConfirmPurchase(null);
            setIsProcessing(false);
          }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-16546">
                  <X className="w-5 h-5" data-id="element-16547" />
                </button>
              </div>

              <div className="p-5 space-y-4" data-id="element-16548">
                <div className="flex items-center gap-4 p-4 bg-indigo-50 rounded-xl" data-id="element-16549">
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0" data-id="element-16550">
                    <Package className="w-6 h-6" data-id="element-16551" />
                  </div>
                  <div data-id="element-16552">
                    <div className="text-lg font-bold text-slate-900" data-id="element-16553">
                      Пакет {confirmPurchase} отчётов
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-16554">
                      Бессрочный доступ
                    </div>
                  </div>
                </div>

                <div className="space-y-3 text-sm" data-id="element-16555">
                  <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-16556">
                    <span className="text-slate-500" data-id="element-16557">Базовая цена</span>
                    <span className={`font-medium ${discount > 0 ? 'line-through text-slate-400' : 'text-slate-900'}`} data-id="element-16558">
                      {formatPrice(BASE_PRICES[confirmPurchase])}
                    </span>
                  </div>
                  {discount > 0 && <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-16559">
                      <span className="text-emerald-600 font-medium" data-id="element-16560">
                        Скидка {discount}% ({discountReason})
                      </span>
                      <span className="text-emerald-600 font-bold" data-id="element-16561">
                        -
                        {formatPrice(BASE_PRICES[confirmPurchase] - getPackagePrice(confirmPurchase, discount))}
                      </span>
                    </div>}
                  <div className="flex justify-between py-3" data-id="element-16562">
                    <span className="text-lg font-bold text-slate-900" data-id="element-16563">
                      Итого к оплате
                    </span>
                    <span className="text-lg font-bold text-indigo-600" data-id="element-16564">
                      {formatPrice(getPackagePrice(confirmPurchase, discount))}
                    </span>
                  </div>
                </div>

                <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg text-xs text-slate-500" data-id="element-16565">
                  <ShieldCheck className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-16566" />
                  <span data-id="element-16567">
                    Гарантия возврата 7 дней. Отчёты из пакета списываются при
                    оформлении заказов.
                  </span>
                </div>
              </div>

              <div className="p-5 border-t border-slate-100 space-y-3" data-id="element-16568">
                <Button variant="primary" fullWidth disabled={isProcessing} onClick={handleConfirmPurchase} className="shadow-md shadow-indigo-500/20" data-id="element-16569">
                  {isProcessing ? <span className="flex items-center gap-2" data-id="element-16570">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-16571" />
                      Обработка оплаты...
                    </span> : <span className="flex items-center gap-2" data-id="element-16572">
                      <CreditCard className="w-4 h-4" data-id="element-16573" />
                      Оплатить{' '}
                      {formatPrice(getPackagePrice(confirmPurchase, discount))}
                    </span>}
                </Button>
                <button onClick={() => {
            setConfirmPurchase(null);
            setIsProcessing(false);
          }} className="w-full text-center text-sm text-slate-500 hover:text-slate-700 transition-colors py-1" data-id="element-16574">
                  Отмена
                </button>
              </div>
            </div>
          </div>, document.body)}

      {/* Purchase Success Modal */}
      {purchaseSuccess && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md overflow-y-auto" data-id="element-16575">
            <div className="bg-white rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.3)] max-w-sm w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200 text-center p-8 ring-1 ring-black/5" data-id="element-16576">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4" data-id="element-16577">
                <Check className="w-8 h-8 text-emerald-600" data-id="element-16578" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-16579">
                Пакет приобретён!
              </h3>
              <p className="text-sm text-slate-500 mb-6" data-id="element-16580">
                Пакет на {purchaseSuccess} отчётов добавлен в ваш аккаунт.
                Используйте отчёты при оформлении заказов.
              </p>
              <Button variant="primary" fullWidth onClick={() => setPurchaseSuccess(null)} data-id="element-16581">
                Отлично
              </Button>
            </div>
          </div>, document.body)}

      {/* Upgrade Plan Modal */}
      {upgradePlan && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md overflow-y-auto" data-id="element-16582">
            <div className="bg-white rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.3)] max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200 ring-1 ring-black/5" data-id="element-16583">
              <div className="p-5 border-b border-slate-100" data-id="element-16584">
                <div className="flex items-center justify-between" data-id="element-16585">
                  <div className="flex items-center gap-2" data-id="element-16586">
                    <Crown className="w-5 h-5 text-amber-500" data-id="element-16587" />
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-16588">
                      Переход на {upgradePlan.name}
                    </h3>
                  </div>
                  <button onClick={() => {
              setUpgradePlan(null);
              setUpgradeProcessing(false);
            }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-16589">
                    <X className="w-5 h-5" data-id="element-16590" />
                  </button>
                </div>
              </div>

              <div className="p-5 space-y-5" data-id="element-16591">
                {/* What you get */}
                <div data-id="element-16592">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-16593">
                    Что вы получите
                  </div>
                  <div className="space-y-1.5" data-id="element-16594">
                    {upgradePlan.included.filter(f => !currentPlan.included.includes(f)).length > 0 ? upgradePlan.included.filter(f => !currentPlan.included.includes(f)).map(feature => <div key={feature} className="flex items-center gap-2 text-sm text-slate-700" data-id="element-16595">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-16596" />
                              {feature}
                            </div>) : upgradePlan.included.map(feature => <div key={feature} className="flex items-center gap-2 text-sm text-slate-700" data-id="element-16597">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-16598" />
                            {feature}
                          </div>)}
                  </div>
                </div>

                {/* Price */}
                <div className="bg-slate-50 rounded-xl p-4 space-y-2" data-id="element-16599">
                  <div className="flex justify-between text-sm" data-id="element-16600">
                    <span className="text-slate-500" data-id="element-16601">
                      Текущий тариф ({currentPlan.name})
                    </span>
                    <span className="text-slate-600" data-id="element-16602">
                      {currentPlan.priceLabel}
                      {currentPlan.period}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-16603">
                    <span className="text-slate-500" data-id="element-16604">
                      Новый тариф ({upgradePlan.name})
                    </span>
                    <span className="font-bold text-slate-900" data-id="element-16605">
                      {upgradePlan.priceLabel}
                      {upgradePlan.period}
                    </span>
                  </div>
                  {upgradePlan.price > currentPlan.price && <div className="flex justify-between text-sm pt-2 border-t border-slate-200" data-id="element-16606">
                      <span className="font-medium text-slate-700" data-id="element-16607">
                        Доплата сейчас
                      </span>
                      <span className="font-bold text-indigo-600" data-id="element-16608">
                        +
                        {(upgradePlan.price - currentPlan.price).toLocaleString('ru-RU')}{' '}
                        ₽
                      </span>
                    </div>}
                </div>

                {/* Payment Method */}
                <div data-id="element-16609">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-16610">
                    Способ оплаты
                  </div>
                  <div className="flex gap-3" data-id="element-16611">
                    <button onClick={() => setUpgradePayMethod('balance')} className={`flex-1 p-3 rounded-xl border text-left transition-all ${upgradePayMethod === 'balance' ? 'border-indigo-500 bg-indigo-50/50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-16612">
                      <div className="flex items-center gap-2 mb-1" data-id="element-16613">
                        <Wallet className="w-4 h-4 text-indigo-600" data-id="element-16614" />
                        <span className="font-bold text-sm text-slate-900" data-id="element-16615">
                          С баланса
                        </span>
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-16616">
                        Доступно:{' '}
                        <span className="font-bold text-slate-700" data-id="element-16617">
                          {accountBalance.toLocaleString('ru-RU')} ₽
                        </span>
                      </div>
                      {upgradePayMethod === 'balance' && accountBalance < upgradePlan.price - currentPlan.price && <div className="text-[10px] text-rose-500 mt-1 flex items-center gap-1" data-id="element-16618">
                            <AlertTriangle className="w-3 h-3" data-id="element-16619" /> Недостаточно
                            средств
                          </div>}
                    </button>
                    <button onClick={() => setUpgradePayMethod('card')} className={`flex-1 p-3 rounded-xl border text-left transition-all ${upgradePayMethod === 'card' ? 'border-indigo-500 bg-indigo-50/50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-16620">
                      <div className="flex items-center gap-2 mb-1" data-id="element-16621">
                        <CreditCard className="w-4 h-4 text-slate-600" data-id="element-16622" />
                        <span className="font-bold text-sm text-slate-900" data-id="element-16623">
                          С карты
                        </span>
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-16624">
                        Visa •••• 4582
                      </div>
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg" data-id="element-16625">
                  <Bell className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" data-id="element-16626" />
                  <div className="text-xs text-amber-700" data-id="element-16627">
                    Тариф будет изменён сразу. Следующее списание{' '}
                    <span className="font-bold" data-id="element-16628">{upgradePlan.priceLabel}</span>{' '}
                    произойдёт в дату продления подписки.
                  </div>
                </div>
              </div>

              <div className="p-5 border-t border-slate-100 space-y-3" data-id="element-16629">
                <Button variant="primary" fullWidth disabled={upgradeProcessing || upgradePayMethod === 'balance' && accountBalance < upgradePlan.price - currentPlan.price} onClick={() => {
            setUpgradeProcessing(true);
            const planName = upgradePlan.name;
            setTimeout(() => {
              setUpgradeProcessing(false);
              setUpgradePlan(null);
              showToast(`Тариф успешно изменён на ${planName}! 🎉`);
            }, 2000);
          }} className="shadow-md shadow-indigo-500/20" data-id="element-16630">
                  {upgradeProcessing ? <span className="flex items-center gap-2" data-id="element-16631">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-16632" />
                      Обработка...
                    </span> : <span className="flex items-center gap-2" data-id="element-16633">
                      <Zap className="w-4 h-4" data-id="element-16634" />
                      {upgradePlan.price > currentPlan.price ? `Оплатить и перейти на ${upgradePlan.name}` : `Перейти на ${upgradePlan.name}`}
                    </span>}
                </Button>
                <button onClick={() => {
            setUpgradePlan(null);
            setUpgradeProcessing(false);
          }} className="w-full text-center text-sm text-slate-500 hover:text-slate-700 transition-colors py-1" data-id="element-16635">
                  Отмена
                </button>
              </div>
            </div>
          </div>, document.body)}
    </div>;
}